import FetchClient from "./FetchClient";
import { getTokensFromStorage } from "./AuthService";
import type { Product, ProductListItem } from "../types/Product";
import { isExtraOnlineRetailer } from "../config/retailers";

export interface PaginatedProductsResponse {
  products: ProductListItem[];
  nextCursor?: string;
  hasMore: boolean;
}

export interface ProductsV2Response {
  products: ProductListItem[];
  totalResults?: number;
}

export interface FeaturedDealItem extends ProductListItem {
  retailer: string;
  currentPrice: number;
  savingsAmount: number;
  discountPercent: number;
}

export interface FeaturedDealsResponse {
  deals: FeaturedDealItem[];
}

export interface ProductsV2PaginatedResponse {
  data: ProductListItem[];
  nextCursor?: string;
  hasMore: boolean;
  totalResults?: number;
}

export interface ProductBrowseRequest {
  retailer: string;
  storeId: string;
  showHotDeals?: boolean;
  showPennyItems?: boolean;
  showCoupons?: boolean;
  searchTokens?: string[];
  savedKeywords?: string[];
  categories?: string[];
  showNewestOnly?: boolean;
  showVariantsOnly?: boolean;
  hideCashback?: boolean;
  minStorePrice?: number;
  maxStorePrice?: number;
  minMsrp?: number;
  maxMsrp?: number;
  minDiscount?: number;
  showInStockOnly?: boolean;
  status?: string;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  limit?: number;
  cursor?: string;
}

export interface ProductBrowseStatusCounts {
  all: number;
  new: number;
  priceDrop: number;
  openBox: number;
  stockUpdate: number;
  withLocation: number;
  withOptions: number;
  pickupAvailable: number;
  pickupUnavailable: number;
  onSale: number;
  soldByWalmart: number;
  soldByProSeller: number;
  lowest: number;
  cashback: number;
  ibotta?: number;
  bogo?: number;
  mixMatch?: number;
  coupon?: number;
}

export interface ProductBrowseResponse {
  products: ProductListItem[];
  total: number;
  statusCounts: ProductBrowseStatusCounts;
  categoryCounts: Array<{ id: string; count: number }>;
  keywordCounts: Record<string, number>;
  storeCounts: Record<string, number>;
  nextCursor?: string;
  hasMore: boolean;
  /** Stores showing their last `fallbackDays` because today's crawl is late. */
  fallbackStoreIds?: string[];
  fallbackDays?: number;
}

function unwrapApiPayload<T>(response: ApiResponse<T> | T): T {
  if (response && typeof response === "object" && "data" in response) {
    return (response as ApiResponse<T>).data;
  }
  return response as T;
}

function normalizeProductBrowseResponse(
  response: ApiResponse<ProductBrowseResponse> | ProductBrowseResponse
): ProductBrowseResponse {
  const payload = unwrapApiPayload(response);
  if (!payload || typeof payload !== "object") {
    throw new Error("Invalid product browse response");
  }
  // An empty result set arrives as null, not [].
  const products = payload.products ?? [];
  if (!Array.isArray(products)) {
    throw new Error("Invalid product browse response");
  }
  return {
    ...payload,
    products,
    total: Number.isFinite(payload.total) ? payload.total : products.length,
    statusCounts: payload.statusCounts || {
      all: products.length,
      new: 0,
      priceDrop: 0,
      openBox: 0,
      stockUpdate: 0,
      withLocation: 0,
      withOptions: 0,
      pickupAvailable: 0,
      pickupUnavailable: 0,
      onSale: 0,
      soldByWalmart: 0,
      soldByProSeller: 0,
      lowest: 0,
      cashback: 0,
      ibotta: 0,
      bogo: 0,
      mixMatch: 0,
      coupon: 0,
    },
    categoryCounts: payload.categoryCounts || [],
    keywordCounts: payload.keywordCounts || {},
    storeCounts: payload.storeCounts || {},
    hasMore: Boolean(payload.hasMore),
  };
}

export async function browseProducts(
  params: ProductBrowseRequest
): Promise<ProductBrowseResponse> {
  const response = await FetchClient.post<ApiResponse<ProductBrowseResponse> | ProductBrowseResponse>(
    "/products/browse",
    params
  );
  return normalizeProductBrowseResponse(response);
}

/**
 * Public online-deal browsing. Valid signed-in sessions are attached only to
 * bypass anonymous abuse controls; expired sessions gracefully fall back to
 * guest access instead of blocking the catalog.
 */
export async function browsePublicProducts(
  params: ProductBrowseRequest
): Promise<ProductBrowseResponse> {
  const response = await FetchClient.post<
    ApiResponse<ProductBrowseResponse> | ProductBrowseResponse
  >("/public/products/browse", params, {}, "optional");
  const payload = normalizeProductBrowseResponse(response);
  return {
    ...payload,
    products: payload.products.map((product) => ({
      ...product,
      upc: "",
      sku: "",
      storeId: "online",
      "storeId-upc": product.productKey ? `online-${product.productKey}` : "",
    })),
  };
}

export interface ProductStoreMatchesResponse {
  products: Product[];
  total: number;
}

export async function getProductStoreMatches(params: {
  retailer: string;
  productId: string;
  showHotDeals?: boolean;
  showPennyItems?: boolean;
  showCoupons?: boolean;
}): Promise<ProductStoreMatchesResponse> {
  const response = await FetchClient.post<ApiResponse<ProductStoreMatchesResponse> | ProductStoreMatchesResponse>(
    "/products/store-matches",
    params
  );
  const payload = unwrapApiPayload(response);
  if (!payload || !Array.isArray(payload.products)) {
    throw new Error("Invalid product store matches response");
  }
  return payload;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
}

export interface GetProductsParams {
  storeId: string;
  category?: string;
  cursor?: string;
  limit?: number;
  showNewestOnly?: boolean;
  retailer?: string;
  showHotDeals?: boolean;
}

export interface GetProductsV2Params {
  retailer?: string;
  showHotDeals?: boolean;
  showPennyItems?: boolean;
  storeId?: string; // For admin users to query specific stores
}

export function getProductsByCategory(categoryId: string): Promise<Product[]> {
  return FetchClient.get<Product[]>(`/products?category=${categoryId}`);
}

// Clean function for fetching products - used by React Query
export async function getProductsByStoreId(
  params: GetProductsParams
): Promise<PaginatedProductsResponse> {
  const searchParams = new URLSearchParams();

  searchParams.append("storeId", params.storeId);

  if (params.category) {
    searchParams.append("category", params.category);
  }

  if (params.cursor) {
    searchParams.append("cursor", params.cursor);
  }

  if (params.limit) {
    searchParams.append("limit", params.limit.toString());
  }

  if (params.showNewestOnly) {
    searchParams.append("showNewestOnly", params.showNewestOnly.toString());
  }

  if (params.retailer) {
    searchParams.append("retailer", params.retailer);
  }

  if (params.showHotDeals) {
    searchParams.append("showHotDeals", params.showHotDeals.toString());
  }

  const requestUrl = `/products?${searchParams.toString()}`;

  const response = await FetchClient.get<
    ApiResponse<PaginatedProductsResponse>
  >(requestUrl);

  return response.data;
}

// Simple function for initial products load - React Query will handle caching
export async function getInitialProducts(
  storeId: string,
  category?: string,
  limit: number = 25,
  showNewestOnly?: boolean,
  retailer?: string,
  showHotDeals?: boolean
): Promise<PaginatedProductsResponse> {
  return getProductsByStoreId({
    storeId,
    category,
    limit,
    showNewestOnly,
    retailer,
    showHotDeals,
  });
}

// Get single product detail by identifier and storeId
export async function getProduct(
  productId: string,
  storeId: string,
  retailer?: string
): Promise<Product> {
  const searchParams = new URLSearchParams();
  searchParams.append("storeId", storeId);
  // Product cards consistently pass their `upc` field as the detail
  // identifier. The API's detail contract names that lookup value `sku` for
  // every online source (including cache-backed and Woot feeds).
  searchParams.append("sku", productId);

  if (retailer) {
    searchParams.append("retailer", retailer);
  }

  const requestUrl = `/product?${searchParams.toString()}`;

  const response = await FetchClient.get<ApiResponse<Product>>(requestUrl);

  return response.data;
}

// Helper function for loading next page
export function getNextPage(
  storeId: string,
  cursor: string,
  category?: string,
  limit: number = 25,
  showNewestOnly?: boolean,
  retailer?: string,
  showHotDeals?: boolean
): Promise<PaginatedProductsResponse> {
  return getProductsByStoreId({
    storeId,
    cursor,
    category,
    limit,
    showNewestOnly,
    retailer,
    showHotDeals,
  });
}

// New V2 API function - gets all products from all user stores
export async function getProductsV2(
  params: GetProductsV2Params
): Promise<ProductsV2Response> {
  const searchParams = new URLSearchParams();

  if (params.retailer) {
    searchParams.append("retailer", params.retailer);
  }

  if (params.showHotDeals) {
    searchParams.append("showHotDeals", params.showHotDeals.toString());
  }

  if (params.showPennyItems) {
    searchParams.append("showPennyItems", params.showPennyItems.toString());
  }

  if (params.storeId) {
    searchParams.append("storeId", params.storeId);
  }

  const requestUrl = `/products-v2?${searchParams.toString()}`;

  const response = await FetchClient.get<
    ApiResponse<ProductsV2PaginatedResponse | ProductListItem[]>
  >(requestUrl);

  // Handle both old format (array) and new format (paginated response)
  if (Array.isArray(response.data)) {
    return { products: response.data, totalResults: response.data.length };
  } else if (response.data && typeof response.data === "object") {
    const paginatedData = response.data as ProductsV2PaginatedResponse;
    return { products: paginatedData.data || [], totalResults: paginatedData.totalResults };
  } else {
    console.warn("Unexpected response format from products-v2:", response.data);
    return { products: [] };
  }
}

// Generate query key for React Query
export function getProductsQueryKey(
  storeId: string,
  category?: string,
  showNewestOnly?: boolean,
  showHotDeals?: boolean
): string[] {
  return [
    "products",
    storeId,
    category || "all",
    showNewestOnly ? "newest" : "all",
    showHotDeals ? "crazy-deals" : "all",
  ];
}

// Generate query key for V2 API
export function getProductsV2QueryKey(
  retailer?: string,
  showHotDeals?: boolean,
  showPennyItems?: boolean,
  storeId?: string // Optional storeId for admin users
): string[] {
  const key = [
    "products-v2",
    retailer || "walmart",
    showHotDeals ? "crazy-deals" : "all",
    showPennyItems ? "penny-items" : "all",
  ];
  // Include storeId in query key if provided (for admin users querying specific stores)
  if (storeId) {
    key.push(`store-${storeId}`);
  }
  return key;
}

// Online products interfaces
export interface OnlineProduct {
  productKey?: string;
  retailer: string;
  upc: string;
  firstInserted?: number;
  lastUpdated: number;
  lastUpdates: number[];
  msrp: number;
  currentPrice: number;
  currentPrices: number[];
  url?: string;
  image: string;
  category: string;
  adjustedCategory?: string;
  discountPercentage: number;
  sku: string;
  title: string;
}

export interface OnlineProductsResponse {
  products: OnlineProduct[];
  nextCursor?: string;
  totalResults?: number;
}

export interface GetOnlineProductsParams {
  retailer: string;
  limit?: number;
  cursor?: string;
}

// Fetch online products (for walmart_online retailer)
export async function getOnlineProducts(
  params: GetOnlineProductsParams
): Promise<OnlineProductsResponse> {
  const searchParams = new URLSearchParams();
  searchParams.append("retailer", params.retailer);
  if (params.limit) {
    searchParams.append("limit", params.limit.toString());
  }
  if (params.cursor) {
    searchParams.append("cursor", params.cursor);
  }

  const requestUrl = `/products-online?${searchParams.toString()}`;
  // Response is directly { products: [...], nextCursor: ... } (not wrapped in data)
  const response = await FetchClient.get<OnlineProductsResponse>(requestUrl);
  
  if (response && Array.isArray(response.products)) {
    return response;
  }
  
  console.warn("Unexpected response format from products-online:", response);
  return { products: [] };
}

// Generate query key for online products
export function getOnlineProductsQueryKey(retailer: string): string[] {
  return ["products-online", retailer];
}

export async function getFeaturedDeals(limit: number = 500): Promise<FeaturedDealsResponse> {
  const searchParams = new URLSearchParams();
  searchParams.append("limit", String(limit));
  const response = await FetchClient.get<ApiResponse<FeaturedDealItem[]>>(
    `/featured-deals?${searchParams.toString()}`
  );
  return { deals: Array.isArray(response.data) ? response.data : [] };
}

export function getFeaturedDealsQueryKey(limit: number = 500): string[] {
  return ["featured-deals", `limit-${limit}`];
}

// "For You" feed interfaces
export interface FeedItem extends ProductListItem {
  feedItemId?: string;
  retailer: string;
  currentPrice: number;
  savingsAmount: number;
  discountPercent: number;
  storeCount?: number;
  reason: string;
  reasonType?: string;
  score: number;
}

export interface FeedResponse {
  items: FeedItem[];
}

export interface HotDealsPageResponse extends FeedResponse {
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
  snapshotId: string;
  builtAt: number;
  categorySections: HotDealsCategorySection[];
  summary: HotDealsSummary;
}

export interface PersonalizedFeedPageResponse extends HotDealsPageResponse {
  profileVersion: number;
  personalizationEnabled: boolean;
}

export interface HotDealsCategorySection {
  category: string;
  count: number;
  averageDiscount: number;
  deals: FeedItem[];
}

export interface HotDealsRetailerCount {
  retailer: string;
  count: number;
}

export interface HotDealsSummary {
  online: number;
  local: number;
  retailers: number;
  highestDiscount: number;
  retailerCounts: HotDealsRetailerCount[];
}

export type FeedFilter = "in_store" | "online" | "clearance" | "open_box" | "under_25" | "best_pct" | "";
export type FeedSort = "latest" | "discount" | "personalized";
export type DealFulfillment = "local" | "online" | "";

export type FeedInteractionType =
  | "impression"
  | "open"
  | "nearby_search"
  | "outbound_click"
  | "save"
  | "alert"
  | "show_less"
  | "hide_item"
  | "hide_retailer";

export interface FeedInteractionEvent {
  eventId: string;
  type: FeedInteractionType;
  itemId: string;
  retailer: string;
  category?: string;
  name?: string;
  price?: number;
  fulfillment?: "online" | "in_store";
  dwellMs?: number;
  occurredAt?: number;
  surface?: string;
}

export interface DiscoveryConfigResponse {
  forYouEnabled: boolean;
}

export interface HawkFindPost {
  postId?: string;
  userId: string;
  photo: string;
  title: string;
  sku: string;
  retailer: string;
  discountPrice: number;
  ogPrice: number;
  timestamp: number;
  totalReactions?: number;
  hasUserReaction?: boolean;
  userReaction?: string;
  reactions?: Record<string, number>;
}

export interface HawkFindsResponse {
  posts: HawkFindPost[];
  nextCursor?: string;
  hasMore?: boolean;
}

export interface HawkFindReactionSummary {
  totalReactions: number;
  hasUserReaction: boolean;
  userReaction?: string;
  reactions?: Record<string, number>;
}

export const HAWK_FIND_REACTIONS = ["❤️", "🔥", "😍", "💯", "😂", "😮", "👍"] as const;

export async function getFeed(
  limit: number = 200,
  filter: FeedFilter = "",
  sort: FeedSort = "latest"
): Promise<FeedResponse> {
  const params = new URLSearchParams();
  params.append("limit", String(limit));
  if (filter) params.append("filter", filter);
  params.append("sort", sort);
  const response = await FetchClient.get<ApiResponse<FeedItem[]>>(
    `/feed?${params.toString()}`
  );
  return { items: Array.isArray(response.data) ? response.data : [] };
}

export function getFeedQueryKey(limit: number = 200, filter: FeedFilter = "", sort: FeedSort = "latest"): string[] {
  return ["feed", `limit-${limit}`, `filter-${filter || "all"}`, `sort-${sort}`];
}

export async function getDiscoveryConfig(): Promise<DiscoveryConfigResponse> {
  const response = await FetchClient.get<ApiResponse<DiscoveryConfigResponse>>("/discovery-config");
  return response.data;
}

export function getDiscoveryConfigQueryKey(): readonly string[] {
  return ["discovery-config"];
}

export async function getPersonalizedFeedPage(
  page: number = 1,
  pageSize: number = 60,
  fulfillment: DealFulfillment = "",
): Promise<PersonalizedFeedPageResponse> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    sort: "personalized",
  });
  if (fulfillment) params.append("filter", fulfillment === "local" ? "in_store" : "online");
  const response = await FetchClient.get<ApiResponse<{
    items?: FeedItem[];
    page?: number;
    pageSize?: number;
    total?: number;
    hasMore?: boolean;
    profileVersion?: number;
    personalizationEnabled?: boolean;
  }>>(`/feed?${params.toString()}`);
  const payload = response.data || {};
  const items = Array.isArray(payload.items) ? payload.items : [];
  return {
    items,
    page: payload.page || page,
    pageSize: payload.pageSize || pageSize,
    total: payload.total || items.length,
    hasMore: Boolean(payload.hasMore),
    snapshotId: `profile-${payload.profileVersion || 0}`,
    builtAt: Date.now(),
    categorySections: [],
    summary: {
      online: 0,
      local: 0,
      retailers: 0,
      highestDiscount: 0,
      retailerCounts: [],
    },
    profileVersion: payload.profileVersion || 0,
    personalizationEnabled: payload.personalizationEnabled !== false,
  };
}

export function getPersonalizedFeedPageQueryKey(
  pageSize: number = 60,
  fulfillment: DealFulfillment = "",
): readonly unknown[] {
  return ["feed", "personalized", `page-size-${pageSize}`, ...(fulfillment ? [`fulfillment-${fulfillment}`] : [])];
}

export async function recordFeedEvents(events: FeedInteractionEvent[]): Promise<void> {
  if (!events.length) return;
  await FetchClient.post("/feed/events", { events });
}

export async function getHotDealsPage(
  page: number = 1,
  pageSize: number = 48,
  sort: FeedSort = "discount",
  snapshotId?: string,
  category: string = "",
  search: string = "",
  fulfillment: DealFulfillment = "",
): Promise<HotDealsPageResponse> {
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("pageSize", String(pageSize));
  params.append("sort", sort);
  if (snapshotId) params.append("snapshotId", snapshotId);
  if (category) params.append("category", category);
  if (search) params.append("search", search);
  if (fulfillment) params.append("fulfillment", fulfillment);
  const response = await FetchClient.get<ApiResponse<HotDealsPageResponse | FeedItem[]>>(
    `/best-deals?${params.toString()}`
  );
  if (Array.isArray(response.data)) {
    return {
      items: response.data,
      page,
      pageSize,
      total: response.data.length,
      hasMore: response.data.length === pageSize,
      snapshotId: "",
      builtAt: 0,
      categorySections: [],
      summary: {
        online: 0,
        local: 0,
        retailers: 0,
        highestDiscount: 0,
        retailerCounts: [],
      },
    };
  }
  return {
    items: Array.isArray(response.data?.items) ? response.data.items : [],
    page: response.data?.page || page,
    pageSize: response.data?.pageSize || pageSize,
    total: response.data?.total || 0,
    hasMore: Boolean(response.data?.hasMore),
    snapshotId: response.data?.snapshotId || "",
    builtAt: response.data?.builtAt || 0,
    categorySections: Array.isArray(response.data?.categorySections) ? response.data.categorySections : [],
    summary: response.data?.summary || {
      online: 0,
      local: 0,
      retailers: 0,
      highestDiscount: 0,
      retailerCounts: [],
    },
  };
}

export function getHotDealsPageQueryKey(
  page: number = 1,
  pageSize: number = 48,
  sort: FeedSort = "discount",
  category: string = "",
  search: string = "",
  fulfillment: DealFulfillment = "",
): string[] {
  return [
    "best-deals-snapshot-v2",
    `page-${page}`,
    `pageSize-${pageSize}`,
    `sort-${sort}`,
    `category-${category || "all"}`,
    `search-${search || "none"}`,
    ...(fulfillment ? [`fulfillment-${fulfillment}`] : []),
  ];
}

export async function getHawkFinds(limit: number = 80, cursor?: string): Promise<HawkFindsResponse> {
  const params = new URLSearchParams();
  params.append("limit", String(limit));
  if (cursor) params.append("cursor", cursor);
  const response = await FetchClient.get<ApiResponse<{
    posts?: HawkFindPost[];
    nextCursor?: string;
    hasMore?: boolean;
  } | HawkFindPost[]>>(
    `/hawk-finds?${params.toString()}`
  );
  if (Array.isArray(response.data)) {
    return { posts: response.data, hasMore: response.data.length === limit };
  }
  const posts = Array.isArray(response.data?.posts) ? response.data.posts : [];
  return {
    posts,
    nextCursor: response.data?.nextCursor,
    hasMore: response.data?.hasMore ?? !!response.data?.nextCursor,
  };
}

export function getHawkFindsQueryKey(limit: number = 80): string[] {
  return ["hawk-finds", `limit-${limit}`];
}

export async function createHawkFind(data: {
  photo: File;
  title: string;
  sku: string;
  retailer: string;
  discountPrice: number;
  ogPrice: number;
}): Promise<HawkFindPost> {
  const formData = new FormData();
  formData.append("photo", data.photo);
  formData.append("title", data.title);
  formData.append("sku", data.sku);
  formData.append("retailer", data.retailer);
  formData.append("discountPrice", String(data.discountPrice));
  formData.append("ogPrice", String(data.ogPrice));

  const tokens = getTokensFromStorage();

  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/v1/admin/hawk-finds`,
    {
      method: "POST",
      headers: {
        Authorization: tokens?.accessToken || "",
        "X-Source": "web",
      },
      body: formData,
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to create Hawk Find");
  }
  const json = await response.json();
  return json.data;
}

export async function setHawkFindReaction(
  postId: string,
  reaction: string
): Promise<HawkFindReactionSummary> {
  const response = await FetchClient.post<ApiResponse<HawkFindReactionSummary>>(
    `/hawk-finds/${encodeURIComponent(postId)}/reaction`,
    { reaction }
  );
  return response.data;
}

export async function deleteHawkFind(postId: string, timestamp: number): Promise<void> {
  await FetchClient.delete<ApiResponse<{ postId: string; status: string }>>(
    `/admin/hawk-finds/${encodeURIComponent(postId)}?timestamp=${encodeURIComponent(String(timestamp))}`
  );
}

// Top deals interface with calculated profit
export interface TopDealProduct extends ProductListItem {
  profit: number;
  profitPercentage: number;
}

// Get top deals sorted by profit (msrp - storePrice)
// Only includes NEW deals (firstInserted today) that were also updated today
export async function getTopDeals(
  retailer: string,
  limit: number = 5
): Promise<TopDealProduct[]> {
  // Get today's midnight timestamp
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayMidnight = today.getTime();

  // Walmart online uses products-online endpoint.
  if (retailer === "walmart_online") {
    const onlineResponse = await getOnlineProducts({ retailer: "WALMART", limit: 100 });
    const onlineProducts = onlineResponse.products || [];

    const dealsWithProfit: TopDealProduct[] = onlineProducts
      .filter((p) => {
        // Must be first inserted today (new deal)
        const firstInserted =
          p.firstInserted ||
          (p.lastUpdates?.length ? Math.min(...p.lastUpdates) : p.lastUpdated) ||
          0;
        return firstInserted >= todayMidnight;
      })
      .map((p) => {
        const currentPrice = p.currentPrices?.[p.currentPrices.length - 1] || p.currentPrice || 0;
        const profit = p.msrp - currentPrice;
        const profitPercentage = p.msrp > 0 ? (profit / p.msrp) * 100 : 0;
        return {
          upc: p.upc,
          sku: p.sku || p.upc,
          name: p.title,
          msrp: p.msrp,
          onlinePrice: p.msrp,
          image: p.image,
          storeId: "",
          "storeId-upc": `online-${p.upc}`,
          storePrice: p.currentPrices.length > 0 ? p.currentPrices : [p.currentPrice],
          backroom: [0],
          salesFloor: [1],
          aisle: "",
          category: p.category,
          adjustedCategory: p.adjustedCategory,
          url: p.url,
          discountPercentage: p.discountPercentage,
          firstInserted:
            p.firstInserted ||
            (p.lastUpdates?.length ? Math.min(...p.lastUpdates) : p.lastUpdated) ||
            0,
          lastUpdates: p.lastUpdates.length > 0 ? p.lastUpdates : [p.lastUpdated],
          inStock: true,
          profit,
          profitPercentage,
        } as TopDealProduct;
      })
      .filter((p) => p.profit > 0 && p.msrp > 0);

    return dealsWithProfit.sort((a, b) => b.profit - a.profit).slice(0, limit);
  }

  // For other retailers - try hot deals first, fall back to regular products.
  // Amazon is online-only in this flow, so always pin storeId to "online".
  const productsV2Params =
    retailer === "amazon" || retailer === "lowes" || retailer === "tractor_supply" || retailer === "northern_tool" || retailer === "kohls" || isExtraOnlineRetailer(retailer)
      ? { retailer, storeId: "online" }
      : { retailer };
  let response = await getProductsV2({ ...productsV2Params, showHotDeals: true });
  let products = response.products || [];

  if (products.length === 0) {
    response = await getProductsV2(productsV2Params);
    products = response.products || [];
  }

  // Filter for NEW deals (firstInserted today) and calculate profit
  const dealsWithProfit: TopDealProduct[] = products
    .filter((p) => {
      // Must be first inserted today (new deal)
      const firstInserted = p.firstInserted || 0;
      return firstInserted >= todayMidnight;
    })
    .map((p) => {
      const storePrice = p.storePrice?.[p.storePrice.length - 1] || 0;
      const profit = p.msrp - storePrice;
      const profitPercentage = p.msrp > 0 ? (profit / p.msrp) * 100 : 0;
      return { ...p, profit, profitPercentage };
    })
    .filter((p) => p.profit > 0 && p.msrp > 0);

  // Sort by profit descending and return top N
  return dealsWithProfit.sort((a, b) => b.profit - a.profit).slice(0, limit);
}

// Query key for top deals
export function getTopDealsQueryKey(retailer: string): string[] {
  return ["top-deals", retailer];
}

// Public product interface (limited data for unauthenticated users)
export interface PublicProduct {
  productKey: string;
  name: string;
  msrp: number;
  onlinePrice: number;
  image: string;
  storeId: string;
  currentPrice: number;
  category: string;
  adjustedCategory?: string;
  url?: string;
  inStock: boolean;
  discountPercentage: number;
  aisle: string;
  lastUpdated: number;
}

export async function getPublicProduct(
  productKey: string
): Promise<PublicProduct> {
  const params = new URLSearchParams({ key: productKey });
  const response = await FetchClient.get<ApiResponse<PublicProduct>>(
    `/public/product?${params.toString()}`,
    {},
    "optional"
  );
  return response.data;
}

export function getProductShareUrl(
  retailer: string,
  sku: string,
  storeId: string
): string {
  const apiBase = import.meta.env.VITE_API_URL || "";
  return `${apiBase}/share/product?retailer=${encodeURIComponent(retailer)}&sku=${encodeURIComponent(sku)}&storeId=${encodeURIComponent(storeId)}`;
}

// Fetch affiliate tracking link (cached by SKU on server). Defaults to walmart for backward compat.
export async function getAffiliateLink(
  sku: string,
  url: string,
  retailer: string = "walmart"
): Promise<string> {
  const params = new URLSearchParams({ sku, url, retailer });
  const response = await FetchClient.get<ApiResponse<{ trackingUrl: string }>>(
    `/affiliate-link?${params.toString()}`
  );
  const link = response.data.trackingUrl;
  return link.startsWith("http") ? link : `https://${link}`;
}

/** Anonymous affiliate resolution for public catalog outbound clicks. */
export async function getPublicAffiliateLink(
  productKey: string
): Promise<string> {
  const params = new URLSearchParams({ key: productKey });
  const response = await FetchClient.get<ApiResponse<{ trackingUrl: string }>>(
    `/public/affiliate-link?${params.toString()}`,
    {},
    "optional"
  );
  const link = response.data.trackingUrl;
  return link.startsWith("http") ? link : `https://${link}`;
}

export const getWalmartAffiliateLink = getAffiliateLink;
